const clamp = value => Math.max(0, Math.min(1, value));
const smooth = value => { const t = clamp(value); return t * t * (3 - 2 * t); };
export const isLiquidEntrance = preset => preset === 'liquid-letters' || preset?.in === 'liquid-letters';
export const isEdgeEntrance = preset => preset === 'edge-letters' || preset?.in === 'edge-letters';
export const liquidEntranceDuration = (fps, durationInFrames) => Math.min(1.8, Math.max(.12, durationInFrames / fps * .6));

// Compress the stagger for long text so the complete headline always settles.
export function liquidLetterState(frame, fps, index, count, durationInFrames = 90) {
  const duration = liquidEntranceDuration(fps, durationInFrames);
  const localDuration = duration * .55;
  const delay = count > 1 ? index / (count - 1) * duration * .45 : 0;
  const progress = clamp((frame / fps - delay) / localDuration);
  const rise = 1 - Math.pow(1 - progress, 3);
  const reveal = smooth((progress - .25) / .65);
  return { progress, opacity:smooth(progress / .16), y:(1 - rise) * .9,
    x:Math.sin(progress * Math.PI * 2 + index * .65) * (1 - progress) * .08,
    rotate:Math.sin(progress * Math.PI * 2 + index * .9) * (1 - progress) * 12,
    glyphOpacity:reveal, blur:(1 - reveal) * .12,
    dropletOpacity:smooth(progress / .15) * (1 - smooth((progress - .4) / .48)),
    scaleX:.55 + .45 * reveal, scaleY:.6 + .4 * reveal + Math.sin(progress * Math.PI) * .45 };
}

export function prepareLiquidLetters(element, animatedDescendants = new Set()) {
  const doc = element.ownerDocument;
  const walker = doc.createTreeWalker(element, 4);
  const textNodes = [];
  while (walker.nextNode()) {
    const text = walker.currentNode;
    if (!text.textContent.trim() || text.parentElement.closest('script,style,textarea,svg,math')) continue;
    let parent = text.parentElement, nested = false;
    while (parent && parent !== element) { if (animatedDescendants.has(parent)) { nested = true; break; } parent = parent.parentElement; }
    if (!nested) textNodes.push(text);
  }
  const letters = [], replacements = [];
  const segmenter = new Intl.Segmenter(undefined, { granularity:'grapheme' });
  for (const text of textNodes) {
    const fragment = doc.createDocumentFragment();
    const inserted = [];
    for (const word of text.textContent.match(/\s+|\S+/gu) || []) {
      if (/^\s+$/u.test(word)) { const space = doc.createTextNode(word); fragment.append(space); inserted.push(space); continue; }
      const wrapper = doc.createElement('span');
      wrapper.setAttribute('data-liquid-word', '');
      wrapper.style.cssText = 'display:inline-block;white-space:nowrap;';
      for (const { segment } of segmenter.segment(word)) {
        const letter = doc.createElement('span');
        letter.setAttribute('data-liquid-letter', '');
        letter.style.cssText = 'display:inline-block;position:relative;transform-origin:50% 75%;';
        const glyph = doc.createElement('span'); glyph.textContent = segment;
        glyph.style.cssText = 'display:inline-block;';
        const drop = doc.createElement('span'); drop.setAttribute('aria-hidden', 'true');
        drop.style.cssText = 'position:absolute;width:.62em;height:.74em;left:50%;top:50%;transform:translate(-50%,-40%);background:currentColor;border-radius:48% 52% 60% 40% / 60% 40% 60% 40%;pointer-events:none;';
        letter.append(glyph, drop); wrapper.append(letter); letters.push({ letter, glyph, drop });
      }
      fragment.append(wrapper); inserted.push(wrapper);
    }
    text.replaceWith(fragment); replacements.push({ text, inserted });
  }
  return { letters, restore() { for (const { text, inserted } of replacements) { inserted[0]?.replaceWith(text); for (const node of inserted.slice(1)) node.remove(); } } };
}

export function applyLiquidLetters(prepared, frame, fps, durationInFrames) {
  prepared.letters.forEach(({ letter, glyph, drop }, index) => {
    const state = liquidLetterState(frame, fps, index, prepared.letters.length, durationInFrames);
    letter.style.transform = `translate(${state.x}em,${state.y}em) rotate(${state.rotate}deg) scale(${state.scaleX},${state.scaleY})`;
    letter.style.opacity = String(state.opacity);
    glyph.style.opacity = String(state.glyphOpacity);
    glyph.style.filter = `blur(${state.blur}em)`;
    drop.style.opacity = String(state.dropletOpacity);
    drop.style.borderRadius = `${45 + 15 * Math.sin(state.progress * Math.PI)}% 55% 62% 38% / 60% 40% 60% 40%`;
  });
}

export function applyEdgeLetters(prepared, frame, fps, durationInFrames) {
  const duration=liquidEntranceDuration(fps,durationInFrames);
  const count=prepared.letters.length;
  // Measure all targets before transforms so later letters never inherit an
  // earlier glyph's animated geometry.
  const targets=prepared.letters.map(({letter})=>letter.getBoundingClientRect());
  prepared.letters.forEach(({letter,glyph,drop},index)=>{
    const delay=count>1?index/(count-1)*duration*.5:0;
    const p=clamp((frame/fps-delay)/(duration*.5));
    const ease=1-Math.pow(1-p,4),remaining=1-ease,rect=targets[index];
    const doc=letter.ownerDocument;
    const w=doc.documentElement.clientWidth,h=doc.documentElement.clientHeight;
    const edge=index%4;
    const dx=edge===0?-rect.right-60:edge===1?w-rect.left+60:Math.sin(index*2.4)*w*.35;
    const dy=edge===2?-rect.bottom-60:edge===3?h-rect.top+60:Math.cos(index*1.7)*h*.18;
    const overshoot=Math.sin(p*Math.PI)*Math.sin(p*Math.PI*2)*8;
    letter.style.transform=`translate(${dx*remaining}px,${dy*remaining+overshoot}px) rotate(${(index%2?1:-1)*100*remaining}deg)`;
    letter.style.opacity=String(smooth(p/.12));glyph.style.opacity='1';drop.style.display='none';
  });
}
